<template>
    <Teleport to="body">
        <Transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            @after-enter="onEnter"
            @after-leave="onLeave"
        >
            <div
                v-if="show"
                class="fixed inset-0 z-50 overflow-y-auto"
                :aria-labelledby="titleId"
                role="dialog"
                aria-modal="true"
                @mousedown="handleBackdropMouseDown"
                @mouseup="handleBackdropMouseUp"
            >
                <!-- Backdrop Overlay -->
                <div
                    class="fixed inset-0 bg-secondary-900/75 dark:bg-black/80 backdrop-blur-sm transition-opacity"
                    aria-hidden="true"
                ></div>

                <!-- Modal Container -->
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <Transition
                        enter-active-class="transition ease-out duration-200"
                        enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                        leave-active-class="transition ease-in duration-150"
                        leave-from-class="opacity-100 translate-y-0 sm:scale-100"
                        leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            v-if="show"
                            ref="modalRef"
                            :class="modalClasses"
                            class="relative transform overflow-hidden rounded-lg bg-white dark:bg-secondary-800 text-left shadow-xl transition-all w-full"
                            @click.stop
                        >
                            <!-- Header -->
                            <div
                                v-if="$slots.header || title || showClose"
                                class="flex items-start justify-between px-6 py-4 border-b border-secondary-200 dark:border-secondary-700"
                            >
                                <h3
                                    :id="titleId"
                                    class="text-lg font-semibold text-secondary-900 dark:text-white"
                                >
                                    <slot name="header">{{ title }}</slot>
                                </h3>

                                <button
                                    v-if="showClose"
                                    ref="closeButtonRef"
                                    type="button"
                                    @click="close"
                                    class="ml-4 text-secondary-400 hover:text-secondary-600 dark:text-secondary-500 dark:hover:text-secondary-300 transition-colors rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-secondary-800 p-1"
                                    :aria-label="closeAriaLabel"
                                >
                                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <!-- Content -->
                            <div
                                :class="[
                                    contentPadding ? 'px-6 py-4' : '',
                                    maxHeight ? 'overflow-y-auto' : '',
                                ]"
                                :style="maxHeight ? { maxHeight: maxHeight } : {}"
                            >
                                <slot />
                            </div>

                            <!-- Footer -->
                            <div
                                v-if="$slots.footer"
                                class="flex items-center justify-end gap-3 px-6 py-4 border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-900/50"
                            >
                                <slot name="footer" :close="close" />
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted, onUnmounted } from 'vue';

const props = defineProps({
    // Controls modal visibility
    show: {
        type: Boolean,
        default: false,
    },
    // Modal title
    title: {
        type: String,
        default: null,
    },
    // Modal size
    size: {
        type: String,
        default: 'md',
        validator: (value) => ['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', 'full'].includes(value),
    },
    // Allow closing on backdrop click
    closeOnBackdrop: {
        type: Boolean,
        default: true,
    },
    // Allow closing on ESC key
    closeOnEscape: {
        type: Boolean,
        default: true,
    },
    // Show close button in header
    showClose: {
        type: Boolean,
        default: true,
    },
    // Add padding to content area
    contentPadding: {
        type: Boolean,
        default: true,
    },
    // Maximum height for content area (with scroll)
    maxHeight: {
        type: String,
        default: null, // e.g., '60vh', '500px'
    },
    // Aria label for close button
    closeAriaLabel: {
        type: String,
        default: 'Close modal',
    },
    // Prevent closing (for forms with unsaved changes)
    preventClose: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['close', 'opened', 'closed']);

// Refs
const modalRef = ref(null);
const closeButtonRef = ref(null);
const previousActiveElement = ref(null);

// Generate unique ID for aria-labelledby
const titleId = computed(() => `modal-title-${Math.random().toString(36).substr(2, 9)}`);

// Modal size classes
const modalClasses = computed(() => {
    const sizeClasses = {
        sm: 'sm:max-w-sm',
        md: 'sm:max-w-md',
        lg: 'sm:max-w-lg',
        xl: 'sm:max-w-xl',
        '2xl': 'sm:max-w-2xl',
        '3xl': 'sm:max-w-3xl',
        '4xl': 'sm:max-w-4xl',
        full: 'sm:max-w-full sm:mx-4 sm:h-[calc(100vh-2rem)]',
    };
    return sizeClasses[props.size] || sizeClasses.md;
});

// Track backdrop click (prevent closing when dragging from inside modal to backdrop)
const backdropMouseDown = ref(false);

const handleBackdropMouseDown = (e) => {
    backdropMouseDown.value = e.target === e.currentTarget;
};

const handleBackdropMouseUp = (e) => {
    if (props.closeOnBackdrop && backdropMouseDown.value && e.target === e.currentTarget) {
        close();
    }
    backdropMouseDown.value = false;
};

// Close modal
const close = () => {
    if (props.preventClose) {
        return;
    }
    emit('close');
};

// Focus trap - get all focusable elements
const getFocusableElements = () => {
    if (!modalRef.value) return [];

    const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
    ];

    return Array.from(modalRef.value.querySelectorAll(focusableSelectors.join(', ')));
};

// Focus trap handler
const handleFocusTrap = (e) => {
    if (!props.show || !modalRef.value) return;

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // If Tab key is pressed
    if (e.key === 'Tab') {
        // If Shift+Tab and focus is on first element, move to last
        if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
        }
        // If Tab and focus is on last element, move to first
        else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
        }
    }
};

// ESC key handler
const handleEscape = (e) => {
    if (e.key === 'Escape' && props.show && props.closeOnEscape) {
        close();
    }
};

// Lock body scroll when modal is open
watch(() => props.show, async (isOpen) => {
    if (isOpen) {
        // Store current active element
        previousActiveElement.value = document.activeElement;

        // Lock body scroll
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = getScrollbarWidth() + 'px';

        // Wait for modal to render
        await nextTick();

        // Focus first focusable element or close button
        const focusableElements = getFocusableElements();
        if (focusableElements.length > 0) {
            // Focus close button if available, otherwise first focusable element
            if (closeButtonRef.value) {
                closeButtonRef.value.focus();
            } else {
                focusableElements[0].focus();
            }
        }
    } else {
        // Restore body scroll
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';

        // Restore focus to previous element
        if (previousActiveElement.value && previousActiveElement.value.focus) {
            previousActiveElement.value.focus();
        }
    }
});

// Get scrollbar width (to prevent layout shift when locking scroll)
const getScrollbarWidth = () => {
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    document.body.appendChild(outer);

    const inner = document.createElement('div');
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode.removeChild(outer);

    return scrollbarWidth;
};

// Transition callbacks
const onEnter = () => {
    emit('opened');
};

const onLeave = () => {
    emit('closed');
};

// Setup event listeners
onMounted(() => {
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleFocusTrap);
});

// Cleanup event listeners
onUnmounted(() => {
    document.removeEventListener('keydown', handleEscape);
    document.removeEventListener('keydown', handleFocusTrap);

    // Ensure body scroll is restored
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
});

// Expose close method
defineExpose({
    close,
});
</script>

<style scoped>
/* Smooth scrolling for content */
[class*="overflow-y-auto"] {
    scrollbar-width: thin;
    scrollbar-color: #9ca3af #e5e7eb;
}

[class*="overflow-y-auto"]::-webkit-scrollbar {
    width: 8px;
}

[class*="overflow-y-auto"]::-webkit-scrollbar-track {
    background: #e5e7eb;
    border-radius: 4px;
}

[class*="overflow-y-auto"]::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
}

[class*="overflow-y-auto"]::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
}

/* Dark mode scrollbar */
.dark [class*="overflow-y-auto"] {
    scrollbar-color: #4b5563 #1f2937;
}

.dark [class*="overflow-y-auto"]::-webkit-scrollbar-track {
    background: #1f2937;
}

.dark [class*="overflow-y-auto"]::-webkit-scrollbar-thumb {
    background: #4b5563;
}

.dark [class*="overflow-y-auto"]::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
}
</style>
